import { ParsedUrlQuery } from 'querystring'
import { Box, List, ListItem, ListItemIcon, ListItemText, Stack } from '@mui/material'
import { FeatureFlagIdExpiringDelegatedUsers, FeatureFlagIdJml } from 'c1gen/features'
import {
    AppV1AppEntitlementUserBindingServiceSearchGrantFeed,
    UserV1UserServiceSetExpiringUserDelegationBindingByAdmin,
    UserV1UserServiceSetUserRoles,
    UserV1UserServiceUpdate,
} from 'c1gen/permissions'
import { AppAvatarWithFetch } from 'components/core/avatar'
import { C1MoreButton, C1MoreButtonOptionProps } from 'components/core/button'
import { DisplayNameDetails } from 'components/core/display-name-details'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { APIError } from 'components/core/errors/api'
import { PageHeader } from 'components/core/page-header'
import { C1FullPageLoadingSpinner } from 'components/core/page-loader/C1FullPageLoadingSpinner'
import { SmartTabsWithRouting } from 'components/core/tabs'
import { HasCapability, useFeatureChecker, useHasPermission } from 'components/no-ui/capability'
import { crumbs } from 'components/no-ui/crumbs/crumbData'
import { GrantFeedColumns } from 'components/pages/entitlement/grant-feed/GrantFeed'
import { GrantFeedDrawerButton } from 'components/pages/entitlement/grant-feed/GrantFeedDrawerButton'
import { ExpiringDelegatedUserDetails } from 'components/pages/profile/expiring-delegates/expiring-delegate-user-details'
import { UserProfileTypes } from 'components/pages/profile/user-profile-types'
import { AccountsListColumns, AccountsListForUser } from 'components/pages/user/details/AccountsListForUser'
import { ChangeRoleRequestDialog } from 'components/pages/user/details/change-role-dialog'
import { DelegatedUserDetails } from 'components/pages/user/details/delegated-user-details'
import { TasksForUser } from 'components/pages/user/details/TasksForUser'
import { UserAvatarUploadDialog } from 'components/pages/user/details/UserAvatarUpload'
import { UserDetail } from 'components/pages/user/details/UserDetail'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { UserValue } from 'lib/actions/user'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { useSimpleModal } from 'lib/modals'
import { useRouter } from 'next/router'
import {
    UpdateUserRequest,
    User,
    UserAttributeMappingSource,
    UserServiceGetRequest,
    UserStatus,
} from 'pbts/c1/api/user/v1/user'
import { FieldMask } from 'pbts/google/protobuf/field_mask'
import { FC, useContext, useMemo } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { getUserApi, updateUserApi } from 'redux_toolkit/users'
import { UserEnrollments } from './UserEnrollments'

type UserDetailsOptionsArgs = {
    intl: IntlShape
    user: UserValue
    openChangeRole: () => void
    openUpdateAvatar: () => void
    updateUserStatus: (value: UserValue) => void
}

const options = ({ openChangeRole, openUpdateAvatar, intl, user, updateUserStatus }: UserDetailsOptionsArgs): C1MoreButtonOptionProps[] => {
    const items: C1MoreButtonOptionProps[] = []
    if (!user) {
        return items
    }
    items.push({
        key: 'components_user_details_update_user_role',
        onClick: openChangeRole,
        disabled: false,
        label: intl.formatMessage({
            id: 'components_user_details_update_option_user_role',
            defaultMessage: 'Change role',
        }),
        permission: UserV1UserServiceSetUserRoles,
    })

    items.push({
        key: 'components_user_details_update_avatar',
        onClick: openUpdateAvatar,
        disabled: false,
        label: intl.formatMessage({
            id: 'user_avatar_update_button',
            defaultMessage: 'Update avatar',
        }),
        permission: UserV1UserServiceUpdate,
    })

    const item: C1MoreButtonOptionProps = {
        key: 'components_user_details_delete_option',
        onClick: (): void => {
            updateUserStatus(user)
        },
        disabled: false,
        label: '',
        permission: UserV1UserServiceUpdate,
    }

    if (user?.status === UserStatus.DISABLED) {
        item.label = intl.formatMessage({
            id: 'components_user_details_update_option_label_enable',
            defaultMessage: 'Activate',
        })
    } else if (user.status === UserStatus.UNKNOWN || user.status === UserStatus.ENABLED) {
        item.label = intl.formatMessage({
            id: 'components_user_details_update_option_label_disable',
            defaultMessage: 'Suspend',
        })
    } else {
        item.label = intl.formatMessage({
            id: 'components_user_details_update_option_label_disable',
            defaultMessage: 'Suspend',
        })
        item.disabled = true
    }

    items.push(item)

    return items
}

const UserAttributeMappingSourceTooltipContent: FC<{
    source: UserAttributeMappingSource | undefined
}> = ({ source }) => {
    if (!source) {
        return null
    }

    return (
        <ListItem disablePadding>
            <ListItemIcon>
                <AppAvatarWithFetch appId={source.appId} />
            </ListItemIcon>
            <ListItemText primary={source.value} />
        </ListItem>
    )
}

const UserAttributeMappingSourcesTooltipContent: FC<{
    sources: UserAttributeMappingSource[] | undefined
}> = ({ sources }) => {
    if (!sources || sources.length === 0) {
        return null
    }

    return (
        <List>
            {sources.map((source) => (
                <UserAttributeMappingSourceTooltipContent source={source} />
            ))}
        </List>
    )
}

type UserAttributeDisplayProps = {
    user: UserValue | null
    value: string
    sources: UserAttributeMappingSource[]
}

export const UserAttributeDisplay: FC<UserAttributeDisplayProps> = ({ user, value, sources }) => {
    if (!user) return null
    if (value === '' || sources.length === 0) return <C1EmptyIndicator />

    const tooltipContent = sources.length > 1
        ? <UserAttributeMappingSourcesTooltipContent sources={sources} />
        : undefined

    return <DisplayNameDetails
        avatar={<AppAvatarWithFetch appId={sources[0].appId} />}
        displayName={value}
        readOnly
        tooltipContent={tooltipContent}
        showTooltip={!!tooltipContent}
    />
}

export const UserDetails: FC = () => {
    const intl = useIntl()
    const router = useRouter()
    const { open } = useSimpleModal()

    const userContext = useContext(UserContext)
    const loggedInUser = userContext.user
    const userID = router.query.user_id as string
    const hasUserUpdatePermission = useHasPermission(UserV1UserServiceUpdate)
    const hasJml = useFeatureChecker(FeatureFlagIdJml)
    const hasExpiringDelegatedUsersFF = useFeatureChecker(FeatureFlagIdExpiringDelegatedUsers)

    const {
        data: user,
        isError,
        error,
        isLoading,
    } = getUserApi.useGet(UserServiceGetRequest.create({ id: userID, expandMask: { paths: ['*'] } }))
    const { mutate: updateUser } = updateUserApi()

    const tabChange = (newValue: string): string => `/admin/directory/users/${userID}/${newValue}`

    const tabFromRouterQuery = (query: ParsedUrlQuery): string | string[] | undefined => query.user_tab

    const actions = useMemo(() => {
        if (!user) {
            return null
        }
        const updateUserStatus = (userDetails: User): void => {
            const status = userDetails.status === UserStatus.DISABLED ? UserStatus.ENABLED : UserStatus.DISABLED

            const updateUserRequest = UpdateUserRequest.create({
                user: { ...userDetails, status },
                updateMask: FieldMask.create({ paths: ['status'] }),
                expandMask: { paths: ['*'] },
            })

            updateUser(updateUserRequest)
        }
        return <Stack direction="row" spacing={1}>
            <HasCapability permission={AppV1AppEntitlementUserBindingServiceSearchGrantFeed}>
                <GrantFeedDrawerButton
                    filter={{ userRefs: [{ id: userID }] }}
                    columns={[GrantFeedColumns.event, GrantFeedColumns.entitlement, GrantFeedColumns.date]}
                />
            </HasCapability>
            <C1MoreButton
                options={options({
                    intl,
                    user,
                    updateUserStatus,
                    openChangeRole: (): void => { open(ChangeRoleRequestDialog, { userId: user.id, currentRoles: user.roles || [] })() },
                    openUpdateAvatar: (): void => { open(UserAvatarUploadDialog, { userId: user.id })() },
                })}
            />
        </Stack>
    }, [user, userID, intl, updateUser, open])

    if (isLoading) {
        return <C1FullPageLoadingSpinner />
    }

    if (isError && error) {
        return <APIError err={error} />
    }

    let tabs = [
        {
            key: 'details',
            title: intl.formatMessage({
                defaultMessage: 'Details',
                id: 'components_user_details_tab_details',
            }),
            tab: <Box>
                {user && <UserDetail user={user} />}
                {user ? (
                    hasExpiringDelegatedUsersFF ? (
                        <HasCapability permission={UserV1UserServiceSetExpiringUserDelegationBindingByAdmin}>
                            <ExpiringDelegatedUserDetails userId={user.id} fromAdmin />
                        </HasCapability>
                    ) : (
                        <HasCapability permission={UserV1UserServiceUpdate}>
                            <DelegatedUserDetails delegatedUser={user.delegatedUser} userId={user.id} />
                        </HasCapability>
                    )
                ) : null}
            </Box>,
        },
    ]

    if (loggedInUser.id === userID ||
        (loggedInUser?.roleIds && (
            loggedInUser?.roleIds.includes(RoleID.SuperAdmin) ||
            loggedInUser?.roleIds.includes(RoleID.ReadOnlySuperAdmin) ||
            loggedInUser?.roleIds.includes(RoleID.AccessRequestAdmin) ||
            loggedInUser?.roleIds.includes(RoleID.AccessRequestHelpdesk)
        ))) {
        tabs = [
            ...tabs,
            {
                key: 'accounts',
                title: intl.formatMessage({
                    defaultMessage: 'Accounts',
                    id: 'components_user_details_tab_accounts',
                }),
                tab: <AccountsListForUser
                    userId={userID}
                    actionsProps={{
                        showMoreOptionsButton: true,
                        showManageGrantsButton: true,
                    }}
                    columns={[
                        AccountsListColumns.account,
                        AccountsListColumns.app,
                        AccountsListColumns.status,
                        AccountsListColumns.accountType,
                        AccountsListColumns.accountRoles,
                        AccountsListColumns.lastLogin,
                        AccountsListColumns.actions,
                    ]}
                />,
            },
            {
                key: 'tasks',
                title: intl.formatMessage({
                    defaultMessage: 'Tasks',
                    id: 'components_user_details_tab_tasks',
                }),
                tab: <TasksForUser userId={userID} />,
            }]
    }

    if (hasJml === true) {
        tabs = [
            ...tabs,
            {
                key: 'enrollments',
                title: intl.formatMessage({
                    defaultMessage: 'Access profiles',
                    id: 'components_user_details_tab_enrollments',
                }),
                tab: <UserEnrollments userId={userID} />,
            },
        ]
    }

    return <>
        <PageHeader
            breadcrumbs={hasUserUpdatePermission
                ? [crumbs['/admin/directory/users']]
                : undefined
            }
            title={<UserDisplayName
                displayNameVariant="h3"
                includeDetails={false}
                user={user}
                readOnly
                avatarSize="lg"
                margin={false}
                avatarIsEditable
            />}
            titleString={user?.displayName}
            actions={actions}
        />
        {user?.id && (
            <Box px={3} pb={2}>
                <UserProfileTypes userId={user.id} />
            </Box>
        )}
        <SmartTabsWithRouting
            menuKey="user-details"
            tabs={tabs}
            onTabChange={tabChange}
            tabFromRouterQuery={tabFromRouterQuery}
            defaultTabKey="details"
        />
    </>
}
